import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"

import { AgentIterationStyles } from "@/components/AgentIteration/AgentIterationStyles.ts"
import { Button } from "@/components/Button/Button.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Faded } from "@/components/Faded/Faded.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Input, INPUT_PADDING_PX } from "@/components/Input/Input.tsx"
import { InputLikeTextarea } from "@/components/ResizeableTextarea/ResizeableTextarea.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { useLazyModel } from "@/hooks/useModel.ts"
import { BranchRegular, GithubFill, Icon20, MergeRequestOpenRegular } from "@/icons/Icons.ts"
import { CreatePrDialogModel } from "@/routes/tasks/TaskViewScene/CreatePrDialogModel.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { AiAgentTask, AiAgentTaskHistoryItem, CPrincipal } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export interface CreatePrDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  taskModel: TaskViewModel
  taskLink: string
  task: AiAgentTask
  author: CPrincipal
  historyItems: AiAgentTaskHistoryItem[]
}

export const CreatePrDialog = (
  {
    isOpen,
    onOpenChange,
    taskModel,
    taskLink,
    task,
    author,
    historyItems
  }: CreatePrDialogProps
) => {
  const [inited, setInited] = useState(false)

  useEffect(() => {
    if (isOpen && !inited) {
      setInited(true)
    }
  }, [inited, isOpen])

  // We intentionally create the model outside CreatePrDialogContent
  // to persist the edits if the dialog is closed and opened again.
  // Model is created lazily to prevent excessive PR summary fetches.
  const model = useLazyModel(isOpen, () => new CreatePrDialogModel(
    taskModel.gitHubModel,
    task,
    author,
    taskLink,
    historyItems,
    onOpenChange
  ), [inited, taskModel.gitHubModel, task, author, taskLink, historyItems, onOpenChange])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
      trigger={null}
      title="Draft New Pull Request in GitHub"
    >
      {model && (
        <CreatePrDialogContent onOpenChange={onOpenChange} taskModel={taskModel} model={model} />
      )}

    </Dialog>
  )
}

const CreatePrDialogContent = observer(({ onOpenChange, taskModel, model }: {
  onOpenChange: (open: boolean) => void
  taskModel: TaskViewModel
  model: CreatePrDialogModel
}) => {
  void taskModel.gitHubModel.fetchShortSummary()

  return (
    <Vertical gap="16px" width="100%">
      <FormField>
        <InputLabel htmlFor="branch-name">Branch name</InputLabel>
        <InputWithIcon>
          <IconLeft>
            <BranchRegular className={Icon20} color="var(--secondary-text-color)" />
          </IconLeft>
          <LeftPaddedInput
            id="branch-name"
            value={model.branchName.get()}
            onChange={model.handleBranchChange}
            placeholder="Your branch name"
            autoFocus
          />
        </InputWithIcon>
        {model.branchError.get() && <ErrorText>{model.branchError.get()}</ErrorText>}
      </FormField>

      {model.targetBranch.get() && (
        <FormField>
          <InputLabel htmlFor="branch-name">Target branch</InputLabel>
          <InputWithIcon>
            <IconLeft>
              <BranchRegular className={Icon20} color="var(--secondary-text-color)" />
            </IconLeft>
            <LeftPaddedInput
              id="target-branch"
              value={model.targetBranch.get() ?? ""}
              disabled={true}
            />
          </InputWithIcon>
          {model.branchError.get() && <ErrorText>{model.branchError.get()}</ErrorText>}
        </FormField>
      )}

      <FormField>
        <InputLabel htmlFor="pr-title">Title</InputLabel>
        <InputWithIcon>
          <IconLeft>
            <MergeRequestOpenRegular className={Icon20} color="var(--secondary-text-color)" />
          </IconLeft>
          <LeftPaddedInput
            id="pr-title"
            value={model.title.get()}
            onChange={model.handleTitleChange}
            placeholder="Pull request title"
          />
        </InputWithIcon>
        {model.titleError.get() && <ErrorText>{model.titleError.get()}</ErrorText>}
      </FormField>

      <FormField>
        <InputLabel htmlFor="pr-description">Description</InputLabel>
        <InputAreaContainer>
          {taskModel.gitHubModel.isLoading.get() && (
            <InputAreaOverlay>
              <Faded
                size={8}
                backgroundColor="transparent"
                className={AgentIterationStyles.secondLevelText_Loading}
              >
                {ellipsize("Generating description")}
              </Faded>
            </InputAreaOverlay>
          )}
          <DescriptionInput
            id="pr-description"
            value={(taskModel.gitHubModel.isLoading.get()) ? "" : model.description.get()}
            disabled={taskModel.gitHubModel.isLoading.get()}
            onChange={model.handleDescriptionChange}
            placeholder={(taskModel.gitHubModel.isLoading.get()) ? "" : "Describe the changes"}
            rows={5}
          />
        </InputAreaContainer>
      </FormField>

      <Horizontal justifyContent="flex-end" gap="8px">
        <Button
          kind="alternative"
          size="large"
          onClick={() => onOpenChange(false)}
          disabled={model.isCreating.get()}
        >
          Cancel
        </Button>
        <Button kind="primary" size="large" onClick={model.submit} disabled={model.isCreating.get()}>
          <Horizontal alignItems="center" gap="8px">
            {!model.isCreating.get() && <GithubFill className={Icon20} />}
            {model.isCreating.get() ? <Spinner text={ellipsize("Opening")} /> : "Continue in GitHub"}
          </Horizontal>
        </Button>
      </Horizontal>
    </Vertical>
  )
})

const FormField = styled(Vertical)`
  gap: 4px;
`

const InputLabel = styled.label`
  color: var(--secondary-text-color);
  font-size: var(--small-font-size);
  font-weight: 500;
`

const ErrorText = styled.span`
  color: var(--error-color);
`

const DescriptionInput = styled(InputLikeTextarea)`
  width: 100%;
  resize: none;
  max-height: 360px;

  &:disabled {
    background: rgb(26 26 28 / 5%);
    border-color: transparent;
  }
`

const InputAreaOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  pointer-events: none;
  padding: 12px ${INPUT_PADDING_PX}px;
`

const InputWithIcon = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`

const InputAreaContainer = styled.div`
  position: relative;
  width: 100%;
`

const ICON_SIZE_PX = 20
const ICON_GAP_PX = 8

const LeftPaddedInput = styled(Input)`
  padding-left: ${INPUT_PADDING_PX + ICON_SIZE_PX + ICON_GAP_PX}px;

  &:disabled {
    background: rgb(26 26 28 / 5%);
    border-color: transparent;
  }
`

const IconLeft = styled.div`
  position: absolute;
  left: ${INPUT_PADDING_PX}px;
  top: 50%;

  transform: translateY(-50%);

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--secondary-text-color);

  pointer-events: none;
`